import { useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { fieldClass, Select } from '@ledgerloom/ui';
import { TagInput } from './TagInput';

// Fields edited where they are shown, saved on their own (docs/design/contacts.md,
// Editing in place). Each reads as plain text until it is hovered or focused;
// only then does it show its box, arrow, or remove buttons. A text field opens on click or Enter, saves on blur or
// Enter, and Escape puts the value back; a choice saves when it changes. The
// status beside each says Saving, Saved, or what went wrong, and a field that
// did not save keeps what was typed. Patterns: forms/text-field,
// forms/selection-input, forms/form-validation.

// What a save resolves to: null when written, or a note to show (it waits for approval).
export type Save = () => Promise<string | null>;
type Status = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; note: string | null } | { kind: 'error'; message: string };

function useSaveStatus() {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const run = async (save: Save): Promise<boolean> => {
    clearTimeout(timer.current);
    setStatus({ kind: 'saving' });
    try {
      const note = await save();
      setStatus({ kind: 'saved', note });
      if (!note) timer.current = setTimeout(() => setStatus({ kind: 'idle' }), 2500);
      return true;
    } catch (e) {
      setStatus({ kind: 'error', message: (e as Error).message || 'Not saved' });
      return false;
    }
  };
  return { status, run, clear: () => setStatus({ kind: 'idle' }) };
}

export function SaveState({ status, id }: { status: Status; id?: string }) {
  const text = status.kind === 'saving' ? 'Saving…' : status.kind === 'saved' ? status.note ?? 'Saved' : status.kind === 'error' ? status.message : '';
  return <span id={id} className={`save-state${status.kind === 'error' ? ' error' : status.kind === 'saved' && status.note ? ' note' : ''}`} role="status" aria-live="polite">{text}</span>;
}

export function InlineText({ label, value, onSave, placeholder = 'Add', type = 'text', inputMode, validate, display, disabled, maxLength = 200 }: {
  label: string; value: string; onSave: (next: string) => Save; placeholder?: string; type?: 'text' | 'email' | 'tel' | 'date' | 'number';
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal'; validate?: (v: string) => string | null; display?: ReactNode; disabled?: boolean; maxLength?: number;
}) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState<string | null>(null);
  const { status, run, clear } = useSaveStatus();
  const button = useRef<HTMLButtonElement>(null);
  const done = useRef(false);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  const open = () => { if (disabled) return; done.current = false; setDraft(value); setInvalid(null); clear(); setEditing(true); };
  const close = (refocus: boolean) => { setEditing(false); if (refocus) setTimeout(() => button.current?.focus(), 0); };
  const commit = async (refocus: boolean) => {
    if (done.current) return;
    const next = draft.trim();
    if (next === value.trim()) { done.current = true; close(refocus); return; }
    const problem = validate?.(next) ?? null;
    if (problem) { setInvalid(problem); return; }
    done.current = true;
    if (await run(onSave(next))) close(refocus);
    else done.current = false;
  };
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void commit(true); }
    if (e.key === 'Escape') { e.preventDefault(); done.current = true; setDraft(value); setInvalid(null); clear(); close(true); }
  };
  const errorId = `${id}-e`;
  const problem = invalid ?? (status.kind === 'error' ? status.message : null);
  if (editing) return (
    <span className="inline-edit editing">
      <label className="sr-only" htmlFor={id}>{label}</label>
      <input id={id} className={`${fieldClass} h-8 px-2.5`} type={type} inputMode={inputMode} value={draft} maxLength={maxLength} autoFocus
        aria-invalid={problem ? true : undefined} aria-describedby={problem ? errorId : undefined}
        onChange={(e) => { setDraft(e.target.value); setInvalid(null); }} onKeyDown={key} onBlur={() => void commit(false)} />
      {problem ? <span id={errorId} className="save-state error" role="alert">{problem}</span> : <SaveState status={status} />}
    </span>
  );
  return (
    <span className="inline-edit">
      <button ref={button} type="button" className={`inline-value${value ? '' : ' empty'}`} disabled={disabled} onClick={open}
        aria-label={`${label}: ${value || 'not set'}. Edit`}>
        <span className="wrap-text">{value ? display ?? value : placeholder}</span><span className="inline-pen" aria-hidden="true">✎</span>
      </button>
      <SaveState status={status} />
    </span>
  );
}

export function InlineSelect({ label, value, options, onSave, disabled, empty }: {
  label: string; value: string; options: Array<[string, string]>; onSave: (next: string) => Save; disabled?: boolean; empty?: string;
}) {
  const [shown, setShown] = useState(value);
  const { status, run } = useSaveStatus();
  useEffect(() => { setShown(value); }, [value]);
  const change = async (next: string) => {
    setShown(next);
    if (!(await run(onSave(next)))) setShown(value);
  };
  // A quiet box until hovered or focused, like the other fields edited in place.
  return (
    <span className="inline-edit">
      <Select label={label} hideLabel value={shown} disabled={disabled || status.kind === 'saving'} onValueChange={(v) => void change(v)}
        options={[...(empty !== undefined ? [{ value: '', label: empty }] : []), ...options.map(([v, t]) => ({ value: v, label: t }))]}
        className="min-w-0 [&_button[data-field]]:h-8 [&_button[data-field]]:border-transparent [&_button[data-field]]:bg-transparent [&_button[data-field]]:px-2 [&_button[data-field]:focus-visible]:border-line [&_button[data-field]:hover:not(:disabled)]:border-line [&_button[data-field]:disabled]:cursor-default [&_button[data-field]:disabled]:opacity-100 [&_button[data-field]_svg]:opacity-0 [&_button[data-field]:hover:not(:disabled)_svg]:opacity-100 [&_button[data-field]:focus_svg]:opacity-100" />
      <SaveState status={status} />
    </span>
  );
}

// Several inputs saved together (an address) when focus leaves the group or on Enter.
export function InlineGroup<T extends Record<string, string>>({ label, value, display, fields, onSave, validate, onCancel, startOpen = false }: {
  label: string; value: T; display: ReactNode; fields: Array<{ key: keyof T & string; label: string; width?: number; inputMode?: 'numeric' | 'text' }>;
  onSave: (next: T) => Save; validate?: (v: T) => string | null; onCancel?: () => void; startOpen?: boolean;
}) {
  const id = useId();
  const [editing, setEditing] = useState(startOpen);
  const [draft, setDraft] = useState<T>(value);
  const [invalid, setInvalid] = useState<string | null>(null);
  const { status, run, clear } = useSaveStatus();
  const button = useRef<HTMLButtonElement>(null);
  const same = (a: T, b: T) => fields.every((f) => (a[f.key] ?? '').trim() === (b[f.key] ?? '').trim());
  const cancel = () => { setDraft(value); setInvalid(null); clear(); setEditing(false); onCancel?.(); setTimeout(() => button.current?.focus(), 0); };
  const commit = async () => {
    if (same(draft, value)) { if (startOpen) onCancel?.(); setEditing(false); return; }
    const problem = validate?.(draft) ?? null;
    if (problem) { setInvalid(problem); return; }
    if (await run(onSave(draft))) setEditing(false);
  };
  const leave = (e: FocusEvent<HTMLFieldSetElement>) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) void commit(); };
  if (editing) return (
    <fieldset className="inline-group" onBlur={leave} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void commit(); } if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}>
      <legend className="sr-only">{label}</legend>
      {fields.map((f, i) => (
        <span key={f.key} className="inline-group-field">
          <label htmlFor={`${id}-${f.key}`} className="small muted">{f.label}</label>
          <input id={`${id}-${f.key}`} className={`${fieldClass} h-8 px-2.5`} style={f.width ? { width: f.width } : undefined} inputMode={f.inputMode} autoFocus={i === 0}
            value={draft[f.key] ?? ''} onChange={(e) => { setDraft({ ...draft, [f.key]: e.target.value }); setInvalid(null); }} />
        </span>
      ))}
      {invalid ? <span className="save-state error" role="alert">{invalid}</span> : <SaveState status={status} />}
    </fieldset>
  );
  return (
    <span className="inline-edit">
      <button ref={button} type="button" className="inline-value" onClick={() => { setDraft(value); clear(); setEditing(true); }} aria-label={`${label}. Edit`}>
        <span className="wrap-text">{display}</span><span className="inline-pen" aria-hidden="true">✎</span>
      </button>
      <SaveState status={status} />
    </span>
  );
}

// Tags saved as each one is added or removed.
export function InlineTags({ label, value, onSave }: { label: string; value: string[]; onSave: (next: string[]) => Save }) {
  const id = useId();
  const [shown, setShown] = useState(value);
  const { status, run } = useSaveStatus();
  useEffect(() => { setShown(value); }, [value.join('\u0000')]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = async (next: string[]) => {
    setShown(next);
    if (!(await run(onSave(next)))) setShown(value);
  };
  return (
    <span className="inline-edit inline-tags">
      <label className="sr-only" htmlFor={id}>{label}</label>
      <TagInput id={id} quiet value={shown} onChange={(t) => void change(t)} placeholder="Add a tag" />
      <SaveState status={status} />
    </span>
  );
}
